import { FC } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { getPriorityColor, getPriorityIcon } from '@/entities/vfo/helpers';
import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { ApiTaskResponseV2 } from '@/shared/api';
import { breakpoints } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

import { useIsFounder } from '../hooks/useIsFounder';
import { useMembers } from '../hooks/useMembers';
import { useTasks } from '../hooks/useTasks';

import { SelectStatus } from './SelectStatus';
import { UserCell } from './UserCell';

type TasksTableProps = {
  tasks: ApiTaskResponseV2[];
  title?: string;
  small?: boolean;
  showAssignee?: boolean;
  minWidth?: number;
  rowsClassName?: string;
};

export const TasksTable: FC<TasksTableProps> = ({
  tasks,
  title,
  small,
  minWidth,
  rowsClassName,
  showAssignee = true,
}) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'tasksTable',
  });
  const { priority, status } = useI18nValues();
  const { getByProfileId, currentUser } = useMembers();
  const { updateTask } = useTasks();
  const isFounder = useIsFounder();

  const modals = useModalsStore();

  const cellClasses = cn({ small });

  return (
    <Container>
      {title && (
        <div>
          <b>{title}</b> ({tasks.length})
        </div>
      )}

      {tasks.length === 0 && (
        <EmptyContainer>
          <Icon name="list-checks" size="s44" />
          <NoTasks>{t('noTasks')}</NoTasks>
        </EmptyContainer>
      )}

      {tasks.length > 0 && (
        <Rows style={{ minWidth }} className={rowsClassName}>
          <Row>
            <CellBold className={cellClasses}>№</CellBold>
            <CellBold className={cellClasses}>{t('taskTitle')}</CellBold>
            <CellBold className={cn(cellClasses, { center: small })}>{t('priority')}</CellBold>
            <CellBold className={cellClasses}>{t('status')}</CellBold>
            {showAssignee && <CellBold className={cellClasses}>{t('assignee')}</CellBold>}
          </Row>

          {tasks.map((item) => {
            const member = item.assignee?.profileId
              ? getByProfileId(item.assignee.profileId)
              : null;

            const canEdit =
              isFounder ||
              currentUser?.profileId === item?.creator.profileId ||
              item?.assignee?.profileId === currentUser?.profileId;

            return (
              <RowClickable
                key={item.id}
                onClick={() => {
                  modals.openModal({
                    type: 'EditTaskModal',
                    taskId: item.id,
                  });
                }}
              >
                <Cell className={cellClasses}>{item.taskNumber}</Cell>
                <Cell className={cellClasses}>
                  <Title>{item.title}</Title>
                </Cell>
                <Cell className={cn(cellClasses, { center: small })}>
                  <Priority>
                    <Icon
                      style={{ color: getPriorityColor(item.priority) }}
                      size="s16"
                      name={getPriorityIcon(item.priority)}
                    />
                    {!small && <span>{priority(item.priority)}</span>}
                  </Priority>
                </Cell>
                <Cell className={cellClasses} onClick={(e) => e.stopPropagation()}>
                  {small ? (
                    <span>{status(item.status)}</span>
                  ) : canEdit ? (
                    <SelectStatus
                      value={item.status}
                      onChange={(value) => {
                        updateTask(item.id, { status: value });
                      }}
                    />
                  ) : (
                    <span>{status(item.status)}</span>
                  )}
                </Cell>
                {showAssignee && (
                  <Cell className={cellClasses} onClick={(e) => e.stopPropagation()}>
                    {member ? <UserCell hideAvatar={small} member={member} /> : t('none')}
                  </Cell>
                )}
              </RowClickable>
            );
          })}
        </Rows>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border: 1px solid ${COLORS.systemDark50A};
  padding: 24px;
  border-radius: 24px;
  max-height: 100%;
  gap: 8px;
  overflow: auto;

  @container ${breakpoints.sm} {
    padding: 0;
    border: none;
    border-radius: 16px;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 100%;

  @container ${breakpoints.sm} {
    padding: 0;
    border: none;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid ${COLORS.systemDark100};
  align-items: center;

  &:last-of-type {
    border-bottom: none;
  }
`;

const RowClickable = styled(Row)`
  cursor: pointer;
  &:hover {
    background: ${COLORS.systemLight200};
    transition: background 0.2s;
  }
`;

const Title = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Cell = styled.div`
  flex: 1;
  padding: 0 12px;
  min-height: 52px;
  display: flex;
  align-items: center;
  min-width: 64px;

  &:nth-child(1) {
    flex: 3;
    word-wrap: anywhere;
  }

  &:nth-child(2) {
    flex: 5;
  }

  &:nth-child(3) {
    flex: 5;
  }

  &:nth-child(4) {
    flex: 5;
  }

  &:nth-child(5) {
    flex: 8;
  }

  &.center {
    justify-content: center;
  }

  &.small {
    &:nth-child(1) {
      flex: 2;
    }

    &:nth-child(3) {
      flex: 2;
      min-width: 74px;
    }

    &:nth-child(4) {
      flex: 4;
    }
    &:nth-child(5) {
      flex: 5;
    }
  }

  @container ${breakpoints.sm} {
    &:nth-child(2) {
      min-width: 150px;
    }

    &:nth-child(3) {
      flex: 2;
      min-width: 74px;
      justify-content: center;

      span {
        display: none;
      }
    }

    &:nth-child(4) {
      min-width: 150px;
    }
  }
`;

const CellBold = styled(Cell)`
  font-weight: 700;
`;

const Priority = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyContainer = styled.div`
  color: ${COLORS.systemDark100};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const NoTasks = styled.span`
  color: ${COLORS.systemDark200};
  font-weight: 700;
`;
